import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthFacade } from '@app2/features/auth/auth.facade';
import { AuthStep } from '@core/models';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { CountdownComponent, CountdownEvent } from 'ngx-countdown';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { OtpComponent } from '@app2/shared/components/otp/otp.component';
import { AnalystService } from '@app/services/analyst/analyst.service';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';
import { AnalyticsDirective } from '@app2/core/directives/analytics/analytics.directive';

@Component({
  selector: 'sens-auth',
  imports: [
    Checkbox,
    InputText,
    CountdownComponent,
    ReactiveFormsModule,
    OtpComponent,
    AnalyticsDirective,
  ],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
  providers: [AuthFacade],
})
export class AuthComponent implements OnInit, OnDestroy {
  private facade = inject(AuthFacade);

  private dialogRef = inject(DynamicDialogRef);

  private formBuilder = inject(FormBuilder);

  private analystService = inject(AnalystService);

  form = this.formBuilder.group({
    email: [null, [Validators.required, Validators.pattern(/\S+@\S{2,}\.\S{2,}/)]],
    code: [null, [Validators.required, Validators.minLength(6)]],
  });

  step = this.facade.step;

  errors = this.facade.errors;

  isLoading = this.facade.isLoading;

  canResendCode = signal<boolean>(false);

  constructor() {
    effect(() => {
      if (this.facade.step() === AuthStep.DONE) {
        this.onClose();
      }
    });
  }

  ngOnInit(): void {
    this.analystService.logEvent(AnalyticsEventsEnum.LoginOrRegisterModalAppear).then();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onStepSet(step: AuthStep) {
    this.analystService.logEvent(AnalyticsEventsEnum.LoginOrRegisterModalTap, {
      type: 'email',
    }).then();
    this.facade.setStep(step);
  }

  countdownEvent($event: CountdownEvent): void {
    this.canResendCode.set($event.action === 'done');
  }

  onCodeGet(): void {
    this.analystService.logEvent(AnalyticsEventsEnum.AuthEmailTap).then();
    this.facade.getCode(this.form.controls.email.value || '');
  }

  onCodeVerify(): void {
    this.analystService.logEvent(AnalyticsEventsEnum.AuthCodeTap).then();
    this.facade.verifyCode(
      this.form.controls.email.value || '',
      this.form.controls.code.value || '',
    );
  }

  onCodeReSent(): void {
    this.canResendCode.set(false);
    this.facade.getCode(this.form.controls.email.value || '');
  }

  authLoginLink() {
    const clickId = localStorage.getItem('clickId');
    return 'https://api-prod.sens.love/api/v1/auth/google/login' + (!!clickId ? `?click_id=${clickId}` : '');
  }

  protected readonly AuthStep = AuthStep;

  protected readonly AnalyticsEventsEnum = AnalyticsEventsEnum;

  ngOnDestroy(): void {
    this.facade.reset();
  }

}
